import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { healthApi } from '@/lib/api';
import { ServerCrash, CheckCircle2, AlertTriangle, Wifi, Database, Zap, RefreshCw, ArrowLeft } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_auth/system')({
  component: SystemHealthPage,
});

// Values that mean "healthy" from the health endpoint
const HEALTHY_VALUES = new Set(['ok', 'connected', 'healthy']);

function isOk(value: string | undefined | null): boolean {
  return !!value && HEALTHY_VALUES.has(value.toLowerCase().trim());
}

export default function SystemHealthPage() {
  const [refetchKey, setRefetchKey] = useState(0);

  const { data: health, isLoading, isError, refetch } = useQuery({
    queryKey: ['health', refetchKey],
    queryFn: healthApi.check,
    refetchInterval: 30_000,
  });

  const services = [
    { label: 'API',      value: health?.status,   icon: <Zap size={16} />,      ok: isOk(health?.status) },
    { label: 'Database', value: health?.db,        icon: <Database size={16} />, ok: isOk(health?.db) },
    { label: 'Redis',    value: health?.redis,     icon: <Wifi size={16} />,     ok: isOk(health?.redis) },
  ];

  const allHealthy = !isLoading && !isError && services.every(s => s.ok);

  return (
    <div className="page">
      <Link to="/settings" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: '#64748b', fontSize: 13, marginBottom: 24, textDecoration: 'none' }}>
        <ArrowLeft size={14} /> Back to Settings
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <ServerCrash size={22} style={{ color: '#6366f1' }} /> System Health
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Real-time status of all platform services · version {health?.version ?? '—'}
          </p>
        </div>
        <button
          onClick={() => { refetch(); setRefetchKey(k => k + 1); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            borderRadius: 8, border: '1px solid #2d3148', background: 'transparent',
            color: '#94a3b8', cursor: 'pointer', fontSize: 12,
          }}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Overall status banner */}
      <div style={{
        marginBottom: 24, padding: '14px 18px', borderRadius: 12,
        background: isLoading ? '#1e293b' : allHealthy ? '#10b98115' : '#ef444415',
        border: `1px solid ${isLoading ? '#2d3148' : allHealthy ? '#10b98130' : '#ef444430'}`,
        display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, fontWeight: 600,
        color: isLoading ? '#64748b' : allHealthy ? '#34d399' : '#f87171',
      }}>
        {isLoading
          ? '⏳ Checking platform status…'
          : isError
            ? <><AlertTriangle size={18} /> Unable to reach the API — check connectivity</>
            : allHealthy
              ? <><CheckCircle2 size={18} /> All systems operational</>
              : <><AlertTriangle size={18} /> One or more services degraded</>
        }
      </div>

      {/* Service cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {services.map(svc => (
          <div key={svc.label} style={{
            padding: '18px 20px', borderRadius: 12,
            border: `1px solid ${isLoading ? '#2d3148' : svc.ok ? '#10b98130' : '#ef444430'}`,
            background: isLoading ? '#1e293b' : svc.ok ? '#10b98108' : '#ef444408',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ color: isLoading ? '#475569' : svc.ok ? '#34d399' : '#f87171' }}>{svc.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#e2e8f0' }}>{svc.label}</span>
            </div>
            <div style={{
              fontSize: 12, fontFamily: 'monospace', fontWeight: 700,
              color: isLoading ? '#475569' : svc.ok ? '#34d399' : '#f87171',
            }}>
              {isLoading ? 'checking…' : svc.value ?? 'unknown'}
            </div>
          </div>
        ))}
      </div>

      {/* Extra detail row */}
      {!isLoading && health && (
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
          <div style={{ padding: '14px 18px', borderRadius: 10, background: '#1e293b', fontSize: 12, color: '#64748b' }}>
            <span style={{ color: '#94a3b8', fontWeight: 600 }}>Uptime</span>
            <span style={{ marginLeft: 12, fontFamily: 'monospace', color: '#94a3b8' }}>
              {Math.floor((health as unknown as { uptime: number }).uptime / 60)}m {(health as unknown as { uptime: number }).uptime % 60}s
            </span>
          </div>
          <div style={{ padding: '14px 18px', borderRadius: 10, background: '#1e293b', fontSize: 12, color: '#64748b' }}>
            <span style={{ color: '#94a3b8', fontWeight: 600 }}>Environment</span>
            <span style={{ marginLeft: 12, fontFamily: 'monospace', color: '#94a3b8' }}>
              {(health as unknown as { environment?: string }).environment ?? '—'}
            </span>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, padding: '14px 18px', borderRadius: 10, background: '#1e293b', fontSize: 12, color: '#64748b' }}>
        API endpoint: <code style={{ fontFamily: 'monospace', color: '#94a3b8' }}>GET /health</code>
        {health?.version && <span style={{ marginLeft: 16 }}>Version: <code style={{ fontFamily: 'monospace', color: '#94a3b8' }}>{health.version}</code></span>}
      </div>
    </div>
  );
}
